import styled, { css } from 'styled-components';
import { media, layout, colors } from '@prisjakt/ui-base';
import { Card as CardComponent, CardActionArea } from '@prisjakt/ui/card';
import { BodySmallText } from '@prisjakt/ui/text/variants';
import { ITableLinkProps } from '../types';

export const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const ActionArea = styled(CardActionArea)`
  flex: 1 1 auto;
  cursor: pointer;
  overflow: visible;
`;

type ColumnProps = {
  expand?: boolean;
};

export const Column = styled.div<ColumnProps>(
  props => css`
    display: flex;
    align-items: stretch;

    ${props.expand &&
      css`
        flex: 2;
      `}
  `
);

interface LinkProps extends ITableLinkProps {
  alignCenter?: boolean;
  alignLeft?: boolean;
  alignRight?: boolean;
  vertical?: boolean;
}

export const Link = styled.a<LinkProps>(
  props => css`

  display: flex;
  flex: 2;
  align-items: center;

  ${!props.featured &&
    css`
      cursor: default;
    `}

  ${props.alignCenter &&
    css`
      justify-content: center;
    `}
  ${props.alignLeft &&
    css`
      justify-content: flex-start;
    `}
  ${props.alignRight &&
    css`
      justify-content: flex-end;
    `}

    &:target {
      outline: ${colors.borderLightGray} dotted 1px;
      outline: -webkit-focus-ring-color auto 1px;
    }
`
);

type SharedRowProps = {
  noExternalUri?: boolean;
  featured?: boolean;
  showReport?: boolean;
  hoverEnabled?: boolean;
  $showCleanOfferExperiment?: boolean;
  $kebab?: boolean;
  $forceMobileView?: boolean;
};

const rowStyles = css`
  line-height: 1.3em;
  position: relative;

  ${(props: SharedRowProps) =>
    props.noExternalUri &&
    css`
      cursor: default;
    `}

  background: ${props => props.theme.bg.primary};

  ${props =>
    !props.featured &&
    css`
      color: ${props.theme.text.secondary};
      &:hover {
        color: ${props.theme.text.secondary};
      }
      background: ${props.theme.colors.base100};
    `}
`;

type RowExperimentProps = {
  expanded?: boolean;
};

export const LeftColumn = styled.div`
  align-items: stretch;
  min-width: ${layout.measurements.spacing200};
`;

export const MiddleColumn = styled.div<SharedRowProps>(
  props => css`
    display: flex;
    flex: 1;
    flex-direction: column;
    height: 100%;
    width: 100%;
    ${(props.featured || props.hoverEnabled) &&
      css`
        &:hover::after {
          box-shadow: 0 2px 10px 0 rgb(0, 0, 0, 0.1), 0 0 0 1px rgb(0, 0, 0, 0);
        }
        ::after {
          transition: box-shadow 200ms ease;
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          pointer-events: none;
          border-radius: 8px;
        }
      `}
  `
);

export const TopMiddleRow = styled.div<SharedRowProps & AlternativeRowProps>(
  props =>
    css`
      display: flex;
      padding: ${layout.measurements.spacing050} 0;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: space-between;
      flex: 1;

      ${!props.$forceMobileView &&
        media.mediumUp`
        padding: ${layout.measurements.spacing050};
        gap: 0;
      `}

      ${props.isAlternative &&
        css`
          gap: ${layout.measurements.spacing050};
        `}
    `
);

export const EndColumn = styled.div<SharedRowProps>(
  props => css`
    display: flex;
    align-items: stretch;
    height: 100%;
    padding: ${layout.measurements.spacing050};

    ${!props.$forceMobileView &&
      media.largeUp`
      padding-right: ${layout.measurements.spacing100};
    `}

    ${!props.$forceMobileView &&
      media.smallOnly`
      padding-left: 0;
    `}

    /* Small mobile resolutions, e.g. Galaxy Fold */
    @media screen and (max-width: 280px) {
      padding-right: ${layout.measurements.spacing100};
    }

    ${!props.featured &&
      css`
        visibility: hidden;
      `}
  `
);

export const RowCard = styled(CardComponent)<
  SharedRowProps & RowExperimentProps
>(
  props => css`
    ${rowStyles}
    flex: 1;
    background: ${props.theme.bg.primary};

    ${!props.featured &&
      !props.expanded &&
      css`
        box-shadow: none;
      `}
  `
);

export const Row = styled.div<SharedRowProps & RowExperimentProps>(
  props => css`
    ${rowStyles};
    border-radius: ${props.expanded ? '8px 8px 0 0' : '8px'};
    display: flex;
    align-items: stretch;

    ${!props.featured &&
      css`
        height: 54px;
      `}

    ${props.expanded &&
      css`
        border-bottom: 1px solid ${props.theme.colors.base200};
      `}
  `
);

export const InfoRow = styled.div`
  background: ${props => props.theme.colors.base100};
  background-color: rgba(0, 0, 0, 0.03);
`;

export const AlternativePricesArea = styled.div<SharedRowProps>(
  props => css`
    background: ${props.theme.colors.base100};
    background-color: rgba(0, 0, 0, 0.03);
    padding: 0.75rem;
    padding-top: 0;
  `
);

export const AlternativePricesInner = styled.div<SharedRowProps>(
  props => css`
    background: ${props.featured
      ? props.theme.colors.base0
      : props.theme.colors.base100};
    border-radius: 8px;
    border: 1px solid ${props.theme.colors.base200};
  `
);

export const AltRow = styled.div<SharedRowProps>(
  props => css`
    display: flex;
    flex: 1;
    border-radius: 8px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }

    ${media.smallOnly`
      border: 1px solid ${props.theme.bg.tertiary};

    `}

    ${media.mediumUp`
      ${props.featured &&
        css`
          &:not(:last-child) {
            border-bottom: 1px solid ${props.theme.colors.base200};
          }
        `}
    `}
  `
);

type AlternativeRowProps = {
  isAlternative?: boolean;
};

export const ReportRow = styled.div`
  ${rowStyles}
  margin-bottom: ${layout.measurements.spacing050};
  margin-top: ${layout.measurements.spacing075};
  padding: 0 ${layout.measurements.spacing100};
  border-radius: 8px;
`;

export const AltReportRow = styled.div`
  ${rowStyles}
  background-color: rgba(0, 0, 0, 0.03);
  padding: 0 ${layout.measurements.spacing100};
  border-bottom: 1px solid ${props => props.theme.colors.base200};

  ${media.smallOnly`
    padding: ${layout.measurements.spacing050};
    border: none;
  `}
`;

export const ProductName = styled(BodySmallText)<
  SharedRowProps & AlternativeRowProps
>(
  props => css`
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${props.featured
      ? props.theme.text.primary
      : props.theme.text.tertiary};
    display: none;

    ${props.isAlternative &&
      css`
        white-space: nowrap;
        display: inline;
      `}

    ${!props.$forceMobileView &&
      media.mediumUp`
      display: inline;
    `}
  `
);

export const ProductNameColumn = styled.div<SharedRowProps>`
  align-items: center;
  flex: 0 1 auto;
  display: grid;
  max-width: 60%;

  ${p =>
    p.$forceMobileView &&
    media.mediumUp`
    display: flex;
    max-width: 65%;
  `}
`;
